import { OrderLoyaltyReward } from '@eliadmin/core/src/components/loyalty/order-loyalty-reward';
import { ProfileRepeatOrderButton } from '@eliadmin/core/src/components/profile/profile-repeat-order-button';
import { resolveImageAlt } from '@eliadmin/core/src/utils/image-alt-utils';
import type { CustomerOrder } from '@eliadmin/core/src/api/customers/customers-types';
import { proformApiQuery } from '@eliadmin/core/src/api/proform/proform-api';
import { type ProformOrder } from '@eliadmin/core/src/api/proform/proform-types';
import { ProfileOrderStatus } from '@eliadmin/core/src/components/profile/profile-order-status';
import { Button } from '@eliadmin/core/src/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTrigger,
} from '@eliadmin/core/src/components/ui/dialog';
import { Separator } from '@eliadmin/core/src/components/ui/separator';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { useCustomerStore } from '@eliadmin/core/src/stores/customer-store';
import { useGlobalStore } from '@eliadmin/core/src/stores/global-store';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import { formatPrice, roundPrice } from '@eliadmin/core/src/utils/price-utils';
import { downloadBase64File } from '@eliadmin/core/src/utils/proform-utils';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { DialogTitle } from '@radix-ui/react-dialog';
import { useQuery } from '@tanstack/react-query';
import Image from '@eliadmin/core/src/components/image/image-wrapped';
import type { PropsWithChildren, ReactNode } from 'react';
import { LuDownload } from 'react-icons/lu';

type ProfileOrderDialogProps = {
  order: ProformOrder;
  eliOrder?: CustomerOrder;
};

export function ProfileOrderDialog(props: PropsWithChildren<ProfileOrderDialogProps>) {
  const { t } = useTranslations();
  const locale = useLocaleStore(s => s.currentLocale);
  const customer = useCustomerStore(s => s.me!.customer);
  const currencySettings = useGlobalStore(s => s.generalLayout.currency_settings);
  const orderDoklad = useQuery({
    queryKey: ['ELI_GET_ORDER', customer.email, props.order.doklad],
    queryFn: () =>
      proformApiQuery('ELI_GET_ORDER', {
        e_mail: customer.email,
        doklad: props.order.doklad,
      }),
    enabled: false,
  });

  const getDruhDokladu = (druh: '10' | '20' | '30') => {
    switch (druh) {
      case '10':
        return 'Poptávka';
      case '20':
        return 'Nabídka';
      case '30':
        return 'Objednávka';
      default:
        return 'Neznámý';
    }
  };

  const getDodano = (dodano: '0' | '1' | '2') => {
    switch (dodano) {
      case '0':
        return 'Ne';
      case '1':
        return 'Ano';
      case '2':
        return 'Částečně';
      default:
        return 'Neznámý';
    }
  };

  return (
    <Dialog>
      <DialogTrigger asChild>{props.children}</DialogTrigger>
      <DialogContent className="flex flex-col gap-4">
        {/* header */}
        <DialogHeader>
          <DialogTitle className="text-left text-2xl font-bold">{t('text17')}</DialogTitle>
        </DialogHeader>
        <Separator />

        {/* image */}
        {!!props.eliOrder?.items.length && (
          <>
            {props.eliOrder?.items.map(item => (
              <div key={item.id} className="flex flex-col items-center gap-4 sm:flex-row">
                <Image
                  src={item.image.url}
                  alt={resolveImageAlt(item?.image, undefined)}
                  width={0}
                  height={0}
                  sizes="(max-width: 100px) 100vw, 100px"
                  className={cn('h-full max-h-[100px] w-full max-w-[100px] object-contain')}
                />

                <div className="flex flex-col items-center gap-4 sm:items-start">
                  <h6 className="font-bold">{item.name}</h6>

                  <span className="text-xl font-bold">
                    {item.quantity}x{' '}
                    {formatPrice(
                      roundPrice(item.price_with_vat || item.price),
                      props.order.mena,
                      currencySettings,
                    )}
                  </span>
                </div>
              </div>
            ))}
            <Separator />
          </>
        )}

        {/* order info */}
        <div className="flex flex-col gap-4">
          <InfoAttribute label={t('text1229')} value={props.order.doklad} />
          <InfoAttribute label={t('text1237')} value={getDruhDokladu(props.order.druh_dokladu)} />
          <InfoAttribute label={t('text63')} value={props.order.objednavka} />
          {!!props.order.datum_por && (
            <InfoAttribute
              label={t('text1234')}
              value={new Date(props.order.datum_por).toLocaleDateString(locale)}
            />
          )}
          <InfoAttribute
            label={t('text1235')}
            value={Number(props.order.odsouhlaseno) === 1 ? 'Ano' : 'Ne'}
          />
          <InfoAttribute label="Dodáno" value={getDodano(props.order.dodano)} />
          <InfoAttribute
            label={t('text1236')}
            value={Number(props.order.vyrzeno_kl) === 1 ? 'Ano' : 'Ne'}
          />
          <InfoAttribute
            label={t('text136')}
            value={formatPrice(
              roundPrice(props.order.cena_bez_dph),
              props.order.mena,
              currencySettings,
            )}
          />
          <InfoAttribute
            label={t('text64')}
            value={formatPrice(roundPrice(props.order.dph), props.order.mena, currencySettings)}
          />
          <InfoAttribute
            label={t('text138')}
            value={formatPrice(
              roundPrice(props.order.cena_celkem),
              props.order.mena,
              currencySettings,
            )}
          />
          {/* <InfoAttribute label="Měna" value={props.order.mena} /> */}
          <InfoAttribute label={t('text245')} value={<ProfileOrderStatus order={props.order} />} />

          {String(props.order.attach) === '1' && (
            <Button
              isLoading={orderDoklad.isLoading}
              onClick={async () => {
                const result = await orderDoklad.refetch();
                if (result.data && result.data.root.order) {
                  const { file_name, att } = result.data.root.order;
                  downloadBase64File(att, file_name);
                }
              }}
              className="gap-2"
            >
              <LuDownload /> {t('text1227')}
            </Button>
          )}
        </div>
        <ProfileRepeatOrderButton orderId={props.eliOrder?.id ?? 0} className="w-full" />

        <OrderLoyaltyReward reward={props.eliOrder?.loyalty_reward} refreshOrders />
      </DialogContent>
    </Dialog>
  );
}

type InfoAttributeProps = {
  label: string;
  value: ReactNode;
};
function InfoAttribute(props: InfoAttributeProps) {
  return (
    <div className="flex flex-row justify-between">
      <span className="text-sm font-bold text-muted-foreground">{props.label}</span>
      <span className="text-sm font-normal">{props.value}</span>
    </div>
  );
}
